import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { NotificationsListResponse } from '~/composables/useNotificationsQuery';

type ListPayload = { success: boolean; data: NotificationsListResponse } | undefined;

export const useMarkAllNotificationsRead = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      $fetch<{ success: boolean; data: { marked: number } }>(`/api/notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authStore.token}` },
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'list'] });
      const snapshots = queryClient.getQueriesData<ListPayload>({ queryKey: ['notifications', 'list'] });

      const nowIso = new Date().toISOString();
      snapshots.forEach(([key, payload]) => {
        if (!payload?.data) return;
        queryClient.setQueryData<ListPayload>(key, {
          ...payload,
          data: {
            ...payload.data,
            items: payload.data.items.map((n) => (n.readAt ? n : { ...n, readAt: nowIso })),
            unreadCount: 0,
          },
        });
      });

      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      context?.snapshots?.forEach(([key, payload]) => {
        queryClient.setQueryData(key, payload);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });
};
